
// Pure geometric core for cross-feature line crossings (Forlæng, Trim mod
// linje, Trim til reference, Sæt punkt ved skæring, self-intersection QA -
// see geometry/lineSplit.js and geometry/topologyQa.js for the callers). No
// DOM/THREE dependency - plain {x,y,z} points, Level-1 unit testable.
//
// ── The one intersection model (stabilisation task §4) ─────────────────────
// Every intersection-based editing tool builds on the SAME primitive:
//   1. solveLineIntersectionXY()   - the XY (top-view) crossing point + params
//   2. classifyIntersection3D()    - is it within the segment/ray bounds?
//                                    also carries each line's OWN Z there
//                                    (zA / zB) so a caller can give a new
//                                    vertex the elevation of the feature it
//                                    belongs to - NEVER the other line's.
//   3. findPlanarCrossings()       - the all-pairs, XY-only convenience the
//                                    editing tools use (a height difference
//                                    between the lines is EXPECTED, not a
//                                    rejection - both follow terrain).
//
// The "planarOnly" third state (crosses in plan, Z gap too large) still
// exists for topology QA / self-intersection scanning, which genuinely care
// whether two lines are physically joined. The EDITING tools deliberately do
// NOT use it - they are plan-geometric operations (§1.3).
//
// Two other single-purpose intersection tests live elsewhere and are
// deliberately NOT merged here: polylineOffset.js's intersectLines2D()
// (infinite-line, THREE.Vector2, miter joins only) and polygonValidity.js's
// segmentsIntersect() (bounded, boolean-only, ring validity only). Neither
// returns the t/s + Z data the callers here need.

const EPS = 1e-9; // denominator-near-zero (parallel-lines) threshold

// ── Tolerances (stabilisation task §10) ───────────────────────────────────
// Named per responsibility. They share a value today (all 2 cm planar) and a
// single user setting ("topology.planarTolerance") still overrides the planar
// ones at call time - but the code no longer uses one nameless number for
// three different decisions.
export const INTERSECTION_TOLERANCE_M = 0.02; // planar bounds slack: does a computed XY crossing land ON the bounded segment / ray being tested
export const ON_VERTEX_TOLERANCE_M = 0.02;    // planar: is a crossing close enough to an EXISTING vertex to reuse it rather than insert a near-duplicate
export const CONNECTION_TOLERANCE_M = 0.02;   // 3D: are two endpoints coincident enough to count as a shared node / mergeable
export const VERTICAL_CONNECTION_TOLERANCE_M = 0.05; // |Δz| at a crossing still counted as a physical connection (QA / self-intersection only)

// Back-compat aliases - existing callers / the settings key still use these
// names. DEFAULT_PLANAR_TOLERANCE_M is the planar family's shared default.
export const DEFAULT_PLANAR_TOLERANCE_M = INTERSECTION_TOLERANCE_M;
export const DEFAULT_VERTICAL_TOLERANCE_M = VERTICAL_CONNECTION_TOLERANCE_M;

// Raw, UNBOUNDED solve of where the two infinite 2D lines through (a1,a2)
// and (b1,b2) cross, in each line's own parametric form:
//   P = a1 + t·(a2-a1) = b1 + s·(b2-b1)
// Returns {t, s, x, y} or null (only when the lines are parallel, including
// collinear/coincident - there is no single crossing point to report).
// Bounds (is t/s actually within the real segment, or a ray, or unbounded)
// are entirely the caller's concern - see classifyIntersection3D() below,
// which is the bounded/gated entry point every other file in this codebase
// should actually use.
export function solveLineIntersectionXY(a1, a2, b1, b2){
	const dax = a2.x - a1.x, day = a2.y - a1.y;
	const dbx = b2.x - b1.x, dby = b2.y - b1.y;
	const denom = dby * dax - dbx * day;
	if(Math.abs(denom) < EPS) return null;

	const dx1 = a1.x - b1.x, dy1 = a1.y - b1.y;
	const t = (dbx * dy1 - dby * dx1) / denom;
	const s = (dax * dy1 - day * dx1) / denom;

	return {t, s, x: a1.x + t * dax, y: a1.y + t * day};
}

// Bounded/gated + 3D-aware intersection classification between line A
// (a1->a2) and line B (b1->b2). `tRange`/`sRange` (default [0,1], i.e. the
// real bounded segments) let a caller test a RAY instead of a segment by
// passing e.g. tRange:[0, Infinity] (see lineSplit.js's
// extendPolylineToReference - the ray starts at the endpoint and points
// outward past it).
//
// `planarTolerance` is bounds slack, converted internally from a world-unit
// distance into each line's own parametric fraction (so a fixed metre
// tolerance behaves consistently regardless of segment length) - NOT a
// "near miss counts as touching" fuzziness on the crossing point itself
// (two straight lines either cross or are parallel; there is no ambiguity
// there). `verticalTolerance` is the actual 3D-connectivity budget.
//
// Returns exactly one of:
//   {kind: "none", reason: "parallel" | "out-of-bounds", ...}
//     - no usable XY crossing within the requested bounds
//   {kind: "planarOnly", t, s, x, y, zA, zB, z, gap3D}
//     - crosses in plan, but |zA - zB| > verticalTolerance (QA / self-cross)
//   {kind: "connected", t, s, x, y, zA, zB, z, gap3D}
//     - crosses in plan and |zA - zB| <= verticalTolerance
// zA / zB are ALWAYS present when there is a crossing: line A's and line B's
// own elevation at the crossing XY. `z` is their midpoint (kept for callers
// that genuinely want a blended value; the editing tools take zA or zB).
export function classifyIntersection3D(a1, a2, b1, b2, options = {}){
	const {
		tRange = [0, 1],
		sRange = [0, 1],
		planarTolerance = INTERSECTION_TOLERANCE_M,
		verticalTolerance = VERTICAL_CONNECTION_TOLERANCE_M,
	} = options;

	const solved = solveLineIntersectionXY(a1, a2, b1, b2);
	if(!solved) return {kind: "none", reason: "parallel"};

	const {t, s, x, y} = solved;

	const lenA = Math.hypot(a2.x - a1.x, a2.y - a1.y);
	const lenB = Math.hypot(b2.x - b1.x, b2.y - b1.y);
	const tSlack = lenA > EPS ? planarTolerance / lenA : 0;
	const sSlack = lenB > EPS ? planarTolerance / lenB : 0;

	const tMin = tRange[0] - tSlack;
	const tMax = tRange[1] === Infinity ? Infinity : tRange[1] + tSlack;
	const sMin = sRange[0] - sSlack;
	const sMax = sRange[1] === Infinity ? Infinity : sRange[1] + sSlack;

	if(t < tMin || t > tMax || s < sMin || s > sMax){
		return {kind: "none", reason: "out-of-bounds", t, s, x, y};
	}

	const zA = a1.z + t * (a2.z - a1.z);
	const zB = b1.z + s * (b2.z - b1.z);
	const gap3D = Math.abs(zA - zB);
	const kind = gap3D <= verticalTolerance ? "connected" : "planarOnly";

	return {kind, t, s, x, y, zA, zB, z: (zA + zB) / 2, gap3D};
}

// Scans every segment of polyline A against every segment of polyline B,
// classifying each pair via classifyIntersection3D with BOTH sides bounded
// ([0,1] - real, already-existing crossings, never a ray). Shared by every
// caller that needs "does this line actually cross that one anywhere" -
// lineSplit.js's trimPolylineToReference()/intersectAndInsert() and
// topologyQa.js's findSelfIntersections() - rather than each looping over
// segment pairs itself.
//
// When `pointsA`/`pointsB` are the SAME array (self-intersection scanning),
// pass `skipAdjacent: true` so a segment is never tested against itself or
// its immediate neighbour, which necessarily touch at their shared vertex -
// that is expected connectivity, not a self-intersection.
//
// Returns every pair whose classification is NOT "none" ({segIndexA,
// segIndexB, ...classifyIntersection3D result}), in scan order - callers
// pick whichever is relevant to them (nearest to a point, first found, all
// of them for a QA report, etc.) rather than this function guessing intent.
export function findSegmentPairIntersections(pointsA, pointsB, options = {}){
	const {skipAdjacent = false, ...classifyOptions} = options;
	const sameArray = pointsA === pointsB;
	const results = [];
	const segCountA = pointsA.length - 1;
	const segCountB = pointsB.length - 1;

	for(let i = 0; i < segCountA; i++){
		for(let j = 0; j < segCountB; j++){
			if(sameArray){
				if(j <= i) continue; // unordered pairs on the same polyline - test each combination once, never a segment against itself
				if(skipAdjacent && j === i + 1) continue; // shares a vertex with segment i - always "touches" there, not a self-intersection
			}
			const result = classifyIntersection3D(pointsA[i], pointsA[i + 1], pointsB[j], pointsB[j + 1], classifyOptions);
			if(result.kind !== "none"){
				results.push({segIndexA: i, segIndexB: j, ...result});
			}
		}
	}

	return results;
}

// The shared XY (top-view) crossing primitive for every intersection-based
// EDITING tool - Trim mod linje, Trim til reference, Sæt punkt ved skæring
// (Forlæng uses classifyIntersection3D directly for its ray). Both polylines
// are treated as bounded segments; a height difference between them at the
// crossing is EXPECTED and never rejects the crossing (stabilisation task
// §1.3 / §4). Each result carries `zA` (polyline A's own elevation at the
// crossing) and `zB` (polyline B's) so the caller gives a new vertex the
// elevation of the feature it belongs to (§1.4).
//
// `planarTolerance` (default INTERSECTION_TOLERANCE_M) is the only knob: the
// bounds slack for "does this crossing land on the segment". Returns the same
// {segIndexA, segIndexB, t, s, x, y, zA, zB, gap3D} shape
// findSegmentPairIntersections() does, in scan order.
export function findPlanarCrossings(pointsA, pointsB, {planarTolerance = INTERSECTION_TOLERANCE_M} = {}){
	return findSegmentPairIntersections(pointsA, pointsB, {
		planarTolerance,
		verticalTolerance: Infinity, // a Z mismatch never rejects an in-plan crossing here
	});
}
